import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { UserPlus, X } from 'lucide-react'
import { shareInvite } from '../lib/inviteShare'
import { safeStorage } from '../lib/safeStorage'
import { Z } from '../lib/zLayers'

// ── InviteNudge ───────────────────────────────────────────────────────────────
// Word-of-mouth is NextEp's growth engine, so we ask for an invite at the moments
// a user has just FELT the value — right after a big import, or after finishing a
// show and minting a card. Copy is UNIVERSAL (the social hook, not "your TV Time
// friends") so it fits every user and never dates. Heavily self-gated so it can
// never nag: it fires only after the celebration cinematic, at most once per
// window, and backs off hard once the user acts or dismisses.
//
// Restyled to the 17 toast language (2026-09-19, same pass as WatchReturnPrompt) —
// was the identical v16 card shape: hardcoded navy gradient, raw zIndex, a popcorn
// emoji that didn't even fit an invite prompt. Now the same color-mix/--toast-*
// tokens as every other toast in the app.
const KEY = 'nextep_invite_prompted_at'
const SUPPRESS_MS   = 10 * 24 * 60 * 60 * 1000  // after showing: don't re-ask for ~10 days
const ACTED_MS      = 90 * 24 * 60 * 60 * 1000  // after they share/dismiss: back off for months

const lastPrompt = () => { try { return Number(safeStorage.get(KEY)) || 0 } catch { return 0 } }
const setPrompt  = (t) => { try { safeStorage.set(KEY, String(t)) } catch {} }

const COPY = {
  import: 'Your whole library, in one place. NextEp is even better with the people you actually watch with.',
  card:   'Another one for the collection. Know someone whose taste you trust? Watch alongside them here.',
}

export function InviteNudge({ user, profile, wl }) {
  const [nudge, setNudge] = useState(null)   // { kind } | null
  const [busy, setBusy]   = useState(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (!user?.id) return
    // Show after the celebration plays (import finale ~6s, card reveal ~9s), and only
    // if we're outside the suppression window and nothing's already up.
    const arm = (kind, delay) => () => {
      const now = Date.now()
      if (now - lastPrompt() < SUPPRESS_MS) return
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        if (now - lastPrompt() < SUPPRESS_MS) return
        setPrompt(Date.now())   // showing counts as a prompt so it self-limits
        setNudge({ kind })
      }, delay)
    }
    const onImport = arm('import', 7000)
    const onCard   = arm('card', 9500)
    window.addEventListener('nextep:import-complete', onImport)
    window.addEventListener('nextep:card-minted', onCard)
    return () => {
      window.removeEventListener('nextep:import-complete', onImport)
      window.removeEventListener('nextep:card-minted', onCard)
      clearTimeout(timerRef.current)
    }
  }, [user?.id])

  // Auto-dismiss if ignored, so it never lingers.
  useEffect(() => {
    if (!nudge) return
    const t = setTimeout(() => setNudge(null), 14000)
    return () => clearTimeout(t)
  }, [nudge])

  if (!nudge || !user) return null

  const close = (acted) => {
    setPrompt(Date.now() + (acted ? ACTED_MS - SUPPRESS_MS : 0))  // acted → longer back-off
    setNudge(null)
  }

  const invite = async () => {
    if (busy) return
    setBusy(true)
    let res = 'failed'
    try { res = await shareInvite(profile, wl) } catch {}
    setBusy(false)
    if (res === 'shared' || res === 'copied') {
      window.dispatchEvent(new CustomEvent('nextep:toast', { detail: {
        title: res === 'copied' ? '🔗 Invite link copied' : '🎉 Thanks for spreading the word',
        accent: 'var(--green)',
        body: res === 'copied' ? 'Paste it to anyone you watch with.' : 'Every friend makes NextEp better.',
      } }))
      close(true)
    } else if (res === 'failed') {
      window.dispatchEvent(new CustomEvent('nextep:toast', { detail: {
        title: 'Couldn’t open the invite', accent: 'var(--amber)', body: 'Try again from Profile → Invite.',
      } }))
      close(false)
    } else {
      close(false)  // cancelled, back off gently, no toast
    }
  }

  return createPortal((
    <div style={{
      position: 'fixed', left: 12, right: 12, bottom: 'calc(var(--nav-h) + 8px)',
      zIndex: Z.toast, maxWidth: 460, margin: '0 auto', fontFamily: 'var(--font)',
      background: 'linear-gradient(135deg, color-mix(in srgb, var(--amber) 14%, #14141f), color-mix(in srgb, var(--amber) 6%, #0d0d16))',
      border: '1px solid color-mix(in srgb, var(--amber) 40%, transparent)', borderRadius: 'var(--toast-radius)',
      boxShadow: '0 14px 38px rgba(0,0,0,0.55), 0 4px 18px rgba(var(--amber-rgb),0.18), inset 0 1px 0 rgba(var(--ink-rgb),0.06)',
      backdropFilter: 'blur(16px) saturate(1.3)', WebkitBackdropFilter: 'blur(16px) saturate(1.3)',
      padding: '12px 15px 13px', '--nxa': 'invNudgeIn 0.3s cubic-bezier(0.22,1.2,0.36,1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          width: 'var(--toast-chip-size)', height: 'var(--toast-chip-size)', borderRadius: 'var(--radius-full)',
          background: 'rgba(var(--amber-rgb),0.14)', border: '1px solid rgba(var(--amber-rgb),0.35)',
        }}><UserPlus size={17} color="var(--amber)" strokeWidth={2.2} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: 'var(--amber)', fontSize: 'var(--toast-eyebrow-size)', fontWeight: 'var(--toast-eyebrow-weight)', letterSpacing: 'var(--toast-eyebrow-tracking)', textTransform: 'uppercase', marginBottom: 3 }}>
            Better with friends
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12.5, lineHeight: 1.45 }}>
            {COPY[nudge.kind] || COPY.card}
          </div>
        </div>
        <button onClick={() => close(false)} aria-label="Dismiss" style={{
          background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 2, flexShrink: 0, lineHeight: 1,
        }}><X size={15} /></button>
      </div>
      <button onClick={invite} disabled={busy} style={{
        width: '100%', marginTop: 11, padding: '11px 0', borderRadius: 'var(--radius)',
        background: 'var(--amber)', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
        color: 'var(--amber-ink)', fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        opacity: busy ? 0.7 : 1, boxShadow: '0 4px 14px rgba(var(--amber-rgb),0.34)',
      }}>
        <UserPlus size={16} strokeWidth={2.4} /> {busy ? 'Opening…' : 'Send an invite'}
      </button>
      <style>{'@keyframes invNudgeIn{0%{opacity:0;transform:translateY(20px) scale(0.97)}100%{opacity:1;transform:translateY(0) scale(1)}}'}</style>
    </div>
  ), document.body)
}
